import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, costFormater, List, Text, useResponsive } from '@common-fe/common-fe';

import { DEFAULT_DATE_FORMAT, EMPTY_FIELD_VALUE } from '@/common/constants';
import { useGetFundIcon } from '@/modules/investments/hooks';
import {
  InvestmentActivitySide,
  TransactionHistoryPayload,
  TransactionStatus,
} from '@/modules/investments/investments.types';
import colors from '@/styles/colors';

interface Props extends TransactionHistoryPayload {
  isHighlighted?: boolean;
}
const TransactionsHistoryItem: React.FC<Props> = ({
  symbol,
  id,
  isHighlighted,
  name,
  shares,
  date,
  status,
  image,
  amount,
  side,
}) => {
  const { isMobile } = useResponsive();
  const { t } = useTranslation();

  const handleGetFundIcon = useGetFundIcon();

  const formattedDate = useMemo(() => {
    if (date) {
      return date.format(DEFAULT_DATE_FORMAT);
    }
    return EMPTY_FIELD_VALUE;
  }, [date]);

  const isCanceledMode = useMemo(() => {
    return status === TransactionStatus.Canceled;
  }, [status]);
  const statusNode = useMemo(() => {
    let text = t('Processing');
    let color = colors.statusColors.needsHelp;

    if (status === TransactionStatus.Canceled) {
      text = t('Canceled');
      color = colors.statusColors.deniedErrorExpired;
    }
    if (status === TransactionStatus.Paid || status === TransactionStatus.Filled) {
      text = t('Completed');
      color = colors.statusColors.paidActiveComplete;
    }
    return (
      <Box direction="row" justify="end" align="center" data-testid="Investment-status-wrapper-id">
        <Text data-testId={`transaction-history_item_${id}_status`} weight={400}>
          {text}
        </Text>
        <div>
          <Box
            width="10px"
            height="10px"
            margin={{ left: 'xs' }}
            background={color}
            style={{ borderRadius: '50%' }}
          />
        </div>
      </Box>
    );
  }, [id, status, t]);

  const isBuyMode = useMemo(() => {
    return side === InvestmentActivitySide.BUY;
  }, [side]);
  const formattedAmount = useMemo(() => {
    let currentAmount = amount < 0 ? amount * -1 : amount;
    if (!isBuyMode) {
      currentAmount = currentAmount * -1;
    }

    return costFormater(currentAmount, {
      isPrecision: true,
      prefix: isBuyMode ? '+$' : '$',
    });
  }, [amount, isBuyMode]);
  if (isMobile) {
    return (
      <List.Item
        direction="column"
        data-testId={`transaction-history_item_${id}`}
        pad="spacing16"
        isHighlighted={isHighlighted}
      >
        <Box fill direction="row" align="center">
          <Box direction="row" fill align="center">
            {handleGetFundIcon({
              image,
              isGreyMode: isCanceledMode,

              isNullMode: true,
            })}
            <Box direction="column" fill margin={{ left: 'xs' }}>
              <List.Item.Text
                color={isCanceledMode ? 'textDisabled' : 'textBody'}
                data-testId={`transaction-history_item_${id}_name`}
                truncate
                weight={700}
              >
                {name}
              </List.Item.Text>
              <List.Item.Text
                color={isCanceledMode ? 'textDisabled' : 'textSecondary'}
                data-testId={`transaction-history_item_${id}_symbol`}
                size="small"
              >
                {symbol}
              </List.Item.Text>
            </Box>
          </Box>
          <div>
            <Box direction="column" align="end">
              <List.Item.Text
                data-testId={`transaction-history_item_${id}_amount`}
                weight={700}
                color={isCanceledMode ? 'textDisabled' : isBuyMode ? 'success' : 'investmentsLoss'}
              >
                {formattedAmount}
              </List.Item.Text>
              <List.Item.Text
                color={isCanceledMode ? 'textDisabled' : 'textBody'}
                data-testId={`transaction-history_item_${id}_date`}
                weight={400}
              >
                {formattedDate}
              </List.Item.Text>
            </Box>
          </div>
        </Box>
        <Box margin={{ vertical: 'spacing12' }} height="1px" width="100%" background="border1" />
        <Box fill direction="row" justify="between">
          <Box fill>
            <List.Item.Text
              color={isCanceledMode ? 'textDisabled' : 'textBody'}
              weight={400}
              data-testId={`transaction-history_item_${id}_shares`}
            >
              Shares: {shares}
            </List.Item.Text>
          </Box>

          <List.Item.Text>{statusNode}</List.Item.Text>
        </Box>
      </List.Item>
    );
  }
  return (
    <List.Item data-testId={`transaction-history_item_${id}`} isHighlighted={isHighlighted}>
      <Box direction="row" style={{ flex: 30 }}>
        {handleGetFundIcon({
          image,
          isGreyMode: isCanceledMode,

          isNullMode: true,
        })}
        <Box direction="column" margin={{ left: 'xs' }}>
          <List.Item.Text
            color={isCanceledMode ? 'textDisabled' : 'textBody'}
            data-testId={`transaction-history_item_${id}_name`}
            truncate
            weight={700}
          >
            {name}
          </List.Item.Text>
          <List.Item.Text
            color={isCanceledMode ? 'textDisabled' : 'textSecondary'}
            data-testId={`transaction-history_item_${id}_symbol`}
            size="small"
          >
            {symbol}
          </List.Item.Text>
        </Box>
      </Box>

      <List.Item.Text
        flex={10}
        data-testId={`transaction-history_item_${id}_amount`}
        weight={700}
        color={isCanceledMode ? 'textDisabled' : isBuyMode ? 'success' : 'investmentsLoss'}
      >
        {formattedAmount}
      </List.Item.Text>

      <List.Item.Text
        flex={10}
        color={isCanceledMode ? 'textDisabled' : 'textBody'}
        weight={400}
        data-testId={`transaction-history_item_${id}_shares`}
      >
        {shares}
      </List.Item.Text>

      <List.Item.Text
        flex={8}
        color={isCanceledMode ? 'textDisabled' : 'textBody'}
        data-testId={`transaction-history_item_${id}_date`}
        weight={400}
      >
        {formattedDate}
      </List.Item.Text>

      <List.Item.Text flex={20}>{statusNode}</List.Item.Text>
    </List.Item>
  );
};
export default TransactionsHistoryItem;
